<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import router from '@/router';
import ProgressFrame from './layout/ProgressFrame.vue';
import HorizontalContentRow from '../components/HorizontalContentRow.vue';
import ReviewLiveConnectionGroupLayout from '../components/phase-terminal/ReviewLiveConnectionGroupLayout.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import ButtonOutlined from '@/shared/components/button/ButtonOutlined.vue';
import LoadingSpinner from '@/shared/components/LoadingSpinner.vue';
import WarningBox from '../components/WarningBox.vue';
import AttentionCircleFilledSVG from '@shared/assets/icon/attention-circle-filled.svg';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import { message } from 'ant-design-vue';
import { checkLiveConnectionPerPhase } from '../utils/output-connections';
import type { PHASE } from '../utils/constants';
import PageLoadFailed from '../components/PageLoadFailed.vue';

const { t } = useI18n();

const commissioningDeviceStore = useCommissioningDeviceStore();
const serialNumber = commissioningDeviceStore.serialNumber;

const isLoading = ref(true);
const pageFailed = ref(false);
const phasesWithoutLiveConnection = ref<PHASE[]>([]);

const detectConnections = async () => {
  isLoading.value = true;
  pageFailed.value = false;

  try {
    await commissioningDeviceStore.fetchTerminalsState();

    if (commissioningDeviceStore.terminalsStateFound) {
      const hasLiveConnectionPerPhase = checkLiveConnectionPerPhase(
        commissioningDeviceStore.outputConnections,
      );
      phasesWithoutLiveConnection.value = Object.entries(hasLiveConnectionPerPhase)
        .filter(([_phase, hasLiveConnection]) => !hasLiveConnection)
        .map(([phase]) => phase as PHASE);
    } else {
      pageFailed.value = true;
    }
  } catch (error) {
    console.log('Failed to detect connections', error);
    pageFailed.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await detectConnections();
});

const goToNext = async () => {
  const validConnections = phasesWithoutLiveConnection.value.length === 0;
  if (validConnections) {
    router.push({ name: 'ConfigureSolshareConnections' });
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    message.error('Each phase must have at least one live connection.');
  }
};
</script>

<template>
  <ProgressFrame pageClass="subsection-page" withBackArrow titleTight subTitleColor="secondary">
    <template #title>{{ t('commissioning.reviewLiveConnections.pageTitle') }}</template>
    <template #subTitle>{{ serialNumber }}</template>

    <template v-if="pageFailed">
      <PageLoadFailed
        title="Failed to detect SolShare connections."
        retryButtonLabel="Detect connections"
        retryButtonDataTestId="refetch-connections-btn"
        @onRetry="detectConnections"
      />
    </template>

    <template v-else>
      <div class="instruction-text">
        <p>SolShare has detected the following live connections.</p>

        <p class="note">
          <AttentionCircleFilledSVG class="note__icon" aria-hidden="true" />
          <span>NOTE: Only detected live connections can be commissioned.</span>
        </p>

        <p>
          If this is not what you expected, safely shut down the system and check electrical
          connection(s). When resolved, select 'Re-detect connections' button.
        </p>
      </div>

      <template v-if="isLoading">
        <LoadingSpinner />
      </template>
      <template v-else>
        <template v-if="phasesWithoutLiveConnection.length > 0">
          <div class="error-container">
            <WarningBox type="error">
              <template #title>SolShare requires at least 1 live connection per phase.</template>
              <template #message>
                No live connections have been detected on
                <span class="error-phase-name">{{ phasesWithoutLiveConnection.join(', ') }}</span
                >. Safely shutdown the system and check electrical connection(s). When resolved,
                select 'Re-detect connections' button.
              </template>
            </WarningBox>
          </div>
        </template>

        <div class="connections-container" data-tn="review-live-connections-container">
          <template v-for="groupNumber in [1, 2, 3, 4, 5]" :key="groupNumber">
            <HorizontalContentRow
              :firstRow="groupNumber === 1"
              :lastRow="groupNumber === 5"
              noSidePadding
            >
              <ReviewLiveConnectionGroupLayout :groupNumber="groupNumber" />
            </HorizontalContentRow>
          </template>
        </div>

        <div class="end-of-page">
          <ButtonPrimary block @click="goToNext" class="next-button" data-tn="next-btn">
            Confirm
          </ButtonPrimary>

          <ButtonOutlined block @click="detectConnections" data-tn="refetch-connections-btn">
            Re-detect connections
          </ButtonOutlined>
        </div>
      </template>
    </template>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.instruction-text {
  margin: @content-side-margin;
  font-weight: @font-weight-medium;
  text-align: center;

  .note {
    color: @color-primary;
    font-weight: @font-weight-bold;

    .note__icon {
      vertical-align: middle;
      margin-right: 0.25rem;
      width: 1.5rem;
      height: 1.5rem;
    }
  }
}

.error-container {
  margin: 0 @content-side-margin @content-side-margin;

  .error-phase-name {
    font-weight: @font-weight-bold;
  }
}

.end-of-page {
  margin: @content-side-margin;

  .next-button {
    margin-bottom: @margin-bottom-button;
  }
}
</style>
